import type { FC } from "react"

import { useTranslation } from "react-i18next"
import { useRecoilValue } from "recoil"
import { css } from "styled-components"

import Button from "~/components/Button"
import { ConfirmModal } from "~/components/ConfirmModal"
import { Wrapper, usePortal } from "~/components/ui"
import {
  currentUserIsDesignRequestUserState,
  userHasRequiredPermissionsState,
} from "~/containers/Proposal/state/user/user-state"
import { Permission } from "~/graphql/client"
import { useTriggerPreventionToast } from "~/hooks/useTriggerPreventionToast"

const ResetButtonCSS = css`
  display: flex;
  justify-content: center;
  align-items: center;
  & > button:disabled,
  & > .blocked {
    :hover {
      box-shadow: none;
    }
  }
`

export const ResetDesignButton: FC<{
  resetRemote: VoidFunction
}> = ({ resetRemote }) => {
  const { t } = useTranslation()
  const currentUserIsDesignUser = useRecoilValue(
    currentUserIsDesignRequestUserState,
  )

  const isUserWhoCanResetDesign = useRecoilValue(
    userHasRequiredPermissionsState([
      Permission.service_bureau_admin,
      Permission.vision_service_bureau_admin,
      Permission.can_reset_design,
    ]),
  )
  const triggerPreventionToast = useTriggerPreventionToast()

  const {
    Portal: ConfirmResetPortal,
    show: confirmResetShow,
    hide: confirmResetHide,
  } = usePortal({
    elementId: `confirm-reset-design`,
  })

  const handleReset = () => {
    confirmResetHide()
    if (!currentUserIsDesignUser) {
      triggerPreventionToast()

      return
    }
    resetRemote()
  }

  if (!isUserWhoCanResetDesign) return <></>

  return (
    <>
      <Wrapper css={ResetButtonCSS}>
        <Button
          onClick={confirmResetShow}
          disabled={false}
          blocked={!currentUserIsDesignUser}
        >
          {t(`reset`)}
        </Button>
      </Wrapper>
      <ConfirmResetPortal>
        <ConfirmModal
          Heading={() => <h4>{t(`resetConfirmation`)}</h4>}
          onClose={() => confirmResetHide()}
          onSubmit={handleReset}
        />
      </ConfirmResetPortal>
    </>
  )
}
